"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.paymentIntegrations = void 0;
exports.validatePaymentViewerPath = validatePaymentViewerPath;
exports.validatePaymentLaunchContext = validatePaymentLaunchContext;
exports.getPaymentIntegration = getPaymentIntegration;
exports.validatePaymentHandoffInstruction = validatePaymentHandoffInstruction;
exports.validatePaymentCheckoutUrl = validatePaymentCheckoutUrl;
exports.validateCheckoutUrl = validateCheckoutUrl;
exports.validateTransferMateCheckoutUrl = validateTransferMateCheckoutUrl;
const class_validator_1 = require("class-validator");
/** Shared route validation; rejects external URLs and paths outside the active property. */
function validatePaymentViewerPath(path, orgId, propertyId) {
    if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
        throw new Error('Invalid payment return path.');
    }
    const url = new URL(path, 'https://viewer.invalid');
    const segments = url.pathname.split('/');
    if (url.origin !== 'https://viewer.invalid' ||
        segments[1] !== String(orgId) ||
        segments[2] !== String(propertyId) ||
        !segments[3] ||
        url.search ||
        url.hash) {
        throw new Error('Payment return path does not match the active application.');
    }
    return url.pathname;
}
function validatePaymentLaunchContext(value) {
    if (!value ||
        typeof value !== 'object' ||
        Array.isArray(value) ||
        JSON.stringify(value).length > 32768) {
        throw new Error('Invalid payment launch context.');
    }
    const input = value;
    const result = {};
    for (const key of [
        'returnLink',
        'websiteId',
        'locale',
        'brandingColors',
        'brandingMedia',
        'theme',
        'sessionOrigin',
    ]) {
        if (input[key] === undefined)
            continue;
        if (typeof input[key] !== 'string')
            throw new Error('Invalid payment launch context.');
        result[key] = input[key];
    }
    if (result.returnLink) {
        const url = new URL(result.returnLink);
        if (url.protocol !== 'https:' || url.username || url.password)
            throw new Error('Invalid return link.');
        result.returnLink = url.origin;
    }
    if (result.websiteId && !/^\d+$/.test(result.websiteId))
        throw new Error('Invalid website.');
    for (const key of ['brandingColors', 'brandingMedia', 'theme']) {
        if (result[key])
            JSON.parse(result[key]);
    }
    if (result.sessionOrigin &&
        !['applicant_self_service', 'entrata_prospect_login', 'lead_profile'].includes(result.sessionOrigin)) {
        throw new Error('Invalid session origin.');
    }
    return result;
}
/** Validate and allow-list request fields before any instruction can be retained. */
function paymentFields(value) {
    if (!value ||
        typeof value !== 'object' ||
        Array.isArray(value) ||
        JSON.stringify(value).length > 32768)
        throw new Error('Invalid payment request.');
    const input = value;
    const payment = {};
    const text = (source, target, key, optional = false) => {
        const field = source[key];
        if (optional && field === undefined)
            return;
        if (typeof field !== 'string' || (!optional && !field.trim()) || field.length > 4096)
            throw new Error('Invalid payment request.');
        target[key] = field;
    };
    const number = (key, integer = false, optional = false) => {
        const field = input[key];
        if (optional && field === undefined)
            return;
        if (typeof field !== 'number' ||
            !Number.isFinite(field) ||
            field < 0 ||
            (integer && (!Number.isSafeInteger(field) || field < 1)))
            throw new Error('Invalid payment request.');
        payment[key] = field;
    };
    number('paymentAmount');
    if (payment.paymentAmount === 0)
        throw new Error('Invalid payment request.');
    number('paymentTypeId', true);
    text(input, payment, 'paymentMemo');
    return { input, payment, text, number };
}
function stripeRequest(value) {
    const { input, payment, text } = paymentFields(value);
    text(input, payment, 'referenceType');
    return payment;
}
function transferMateRequest(value) {
    const { input, payment, text, number } = paymentFields(value);
    number('paymentMethodId', true);
    for (const key of ['countryCode', 'paymentPurpose', 'whoIsMakingPayment', 'eduInstituteName'])
        text(input, payment, key);
    for (const key of ['stateCode', 'indiaSourceOfFund'])
        text(input, payment, key, true);
    for (const key of ['indiaFeeExceed', 'indiaDeclarationAmt'])
        number(key, false, true);
    for (const key of ['agreesToTerms', 'agreesToFees', 'agreesToDuplicate']) {
        if (input[key] !== true)
            throw new Error('Payment agreements are required.');
        payment[key] = true;
    }
    for (const key of ['studentDetails', 'payerDetails']) {
        const source = input[key];
        if (!source || typeof source !== 'object' || Array.isArray(source))
            throw new Error('Invalid payer details.');
        const details = {};
        const fields = source;
        for (const field of ['name', 'city', 'address', 'postalCode', 'phoneNumber', 'email'])
            text(fields, details, field);
        if (!(0, class_validator_1.isEmail)(details.email))
            throw new Error('Invalid payer email.');
        for (const field of key === 'studentDetails' ? ['country', 'dob'] : ['nationality'])
            text(fields, details, field);
        for (const field of [
            'chineseName',
            'document',
            ...(key === 'studentDetails' ? ['number'] : ['relationshipToStudent']),
        ])
            text(fields, details, field, true);
        payment[key] = details;
    }
    return payment;
}
/** Capabilities and validation belong to the integration, never to the handoff transport. */
exports.paymentIntegrations = {
    stripe: {
        requiresTopLevel: true,
        checkoutOrigin: 'https://checkout.stripe.com',
        validatePayment: stripeRequest,
    },
    transfermate: {
        requiresTopLevel: true,
        checkoutOrigin: 'https://entrata-api.transfermateeducation.com',
        validatePayment: transferMateRequest,
    },
};
function getPaymentIntegration(processor) {
    if (typeof processor !== 'string' ||
        !Object.prototype.hasOwnProperty.call(exports.paymentIntegrations, processor))
        throw new Error('Unsupported payment processor.');
    return exports.paymentIntegrations[processor];
}
function validatePaymentHandoffInstruction(processor, value) {
    return {
        processor,
        payment: getPaymentIntegration(processor).validatePayment(value),
    };
}
function validatePaymentCheckoutUrl(processor, value) {
    const url = new URL(value);
    if (url.origin !== getPaymentIntegration(processor).checkoutOrigin ||
        url.username ||
        url.password)
        throw new Error('Invalid payment checkout URL.');
    return url.toString();
}
function validateCheckoutUrl(value) {
    return validatePaymentCheckoutUrl('stripe', value);
}
function validateTransferMateCheckoutUrl(value) {
    return validatePaymentCheckoutUrl('transfermate', value);
}
